import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';

type UiState = 'loading' | 'ok' | 'rejected' | 'canceled' | 'error';

type ConfirmResult = {
  status: string;
  ok?: boolean;
  message?: string;
};

export default function PagarServicioReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<UiState>('loading');
  const [message, setMessage] = useState('Confirmando pago...');

  const tokenWs = useMemo(() => searchParams.get('token_ws') || '', [searchParams]);
  const intentoId = useMemo(() => searchParams.get('intento_id') || '', [searchParams]);
  const canceled = useMemo(() => searchParams.get('canceled') === '1', [searchParams]);
  const withError = useMemo(() => searchParams.get('error') === '1', [searchParams]);
  const paymentType = useMemo(() => searchParams.get('tipo') || 'saas', [searchParams]);
  const confirmLockKey = useMemo(
    () => `saas_confirm_lock_${tokenWs}_${intentoId || 'no-intento'}`,
    [tokenWs, intentoId]
  );
  const confirmResultKey = useMemo(
    () => `saas_confirm_result_${tokenWs}_${intentoId || 'no-intento'}`,
    [tokenWs, intentoId]
  );

  useEffect(() => {
    let active = true;

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const readCachedResult = (): ConfirmResult | null => {
      const raw = sessionStorage.getItem(confirmResultKey);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as ConfirmResult;
      } catch {
        return null;
      }
    };

    const writeCachedResult = (result: ConfirmResult) => {
      sessionStorage.setItem(confirmResultKey, JSON.stringify(result));
    };

    const applyResult = (result: ConfirmResult) => {
      if (!active) return;

      const status = String(result.status || '').toLowerCase();

      if (status === 'authorized' && result.ok) {
        setState('ok');
        setMessage('Pago confirmado correctamente.');
        setTimeout(() => navigate(paymentType === 'coach_plan' ? '/horarios?payment=ok' : '/pagar-servicio?payment=ok', { replace: true }), 800);
        return;
      }

      if (status === 'canceled') {
        setState('canceled');
        setMessage(result.message || 'Pago cancelado.');
        setTimeout(() => navigate(paymentType === 'coach_plan' ? '/horarios?payment=canceled' : '/pagar-servicio?payment=canceled', { replace: true }), 900);
        return;
      }

      if (status === 'rejected') {
        setState('rejected');
        setMessage(result.message || 'El pago fue rechazado.');
        setTimeout(() => navigate(paymentType === 'coach_plan' ? '/horarios?payment=rejected' : '/pagar-servicio?payment=rejected', { replace: true }), 900);
        return;
      }

      if (status === 'processing') {
        setState('loading');
        setMessage(result.message || 'Pago en procesamiento. Actualiza en unos segundos.');
        return;
      }

      setState('error');
      setMessage(result.message || 'No se pudo confirmar el pago.');
    };

    const run = async () => {
      if (canceled) {
        applyResult({ status: 'canceled', ok: false, message: 'Pago cancelado por el usuario.' });
        return;
      }

      if (withError || !tokenWs) {
        applyResult({ status: 'error', ok: false, message: 'No se pudo completar el retorno de Transbank.' });
        return;
      }

      const cached = readCachedResult();
      if (cached) {
        applyResult(cached);
        return;
      }

      // Avoid duplicate confirms in dev (React StrictMode) and reload/double-click.
      if (sessionStorage.getItem(confirmLockKey) === '1') {
        setState('loading');
        setMessage('Confirmacion en curso...');

        const started = Date.now();
        while (sessionStorage.getItem(confirmLockKey) === '1' && Date.now() - started < 8000) {
          await sleep(150);
        }

        const cachedAfterWait = readCachedResult();
        if (cachedAfterWait) {
          applyResult(cachedAfterWait);
          return;
        }

        // Stale lock fallback.
        sessionStorage.removeItem(confirmLockKey);
      }

      try {
        sessionStorage.setItem(confirmLockKey, '1');
        setState('loading');
        setMessage('Confirmando pago...');

        const { data, error } = await supabase.functions.invoke(paymentType === 'coach_plan' ? 'coach-plan-confirm' : 'saas-payments-confirm', {
          body: {
            token_ws: tokenWs,
            intento_id: intentoId || undefined,
          },
        });

        if (error) {
          const response = (error as any)?.context;
          let detail = error.message || 'No se pudo confirmar el pago';
          let statusFromError = '';

          if (response && typeof response.json === 'function') {
            const payload = await response.json().catch(() => null);
            statusFromError = String(payload?.status || '').toLowerCase();

            if (payload?.error) {
              detail = payload.error;
              if (payload?.details) {
                detail += `: ${typeof payload.details === 'string' ? payload.details : JSON.stringify(payload.details)}`;
              }
            } else if (payload?.message) {
              detail = payload.message;
            }
          }

          if (statusFromError === 'processing') {
            const result: ConfirmResult = {
              status: 'processing',
              ok: false,
              message: 'Pago en procesamiento. Actualiza en unos segundos.',
            };
            writeCachedResult(result);
            applyResult(result);
            return;
          }

          throw new Error(detail);
        }

        const result: ConfirmResult = {
          status: String((data as any)?.status || ''),
          ok: Boolean((data as any)?.ok),
        };

        writeCachedResult(result);
        applyResult(result);
      } catch (err: any) {
        const result: ConfirmResult = {
          status: 'error',
          ok: false,
          message: err?.message || 'Error al confirmar pago.',
        };

        writeCachedResult(result);
        applyResult(result);
      } finally {
        sessionStorage.removeItem(confirmLockKey);
        setTimeout(() => {
          sessionStorage.removeItem(confirmResultKey);
        }, 15000);
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [tokenWs, intentoId, canceled, withError, navigate, confirmLockKey, confirmResultKey, paymentType]);

  const color =
    state === 'ok'
      ? 'text-green-700 bg-green-50 border-green-200'
      : state === 'loading'
      ? 'text-sky-700 bg-sky-50 border-sky-200'
      : state === 'canceled'
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-red-700 bg-red-50 border-red-200';

  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pago de Servicio</h1>
        <p className="text-gray-600 mb-6">Procesando retorno de Transbank</p>

        <div className={`border rounded-xl px-4 py-4 flex items-center gap-3 ${color}`}>
          <i className="ri-bank-card-line text-xl"></i>
          <span>{message}</span>
        </div>

        <div className="mt-6">
          <button
            onClick={() => navigate('/pagar-servicio')}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Volver a Pagar Servicio
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
